import * as React from 'react';
import { IDatePickerProps, DatePicker } from '@agilepoint/wat-react-ux';
import { memoizeFunction } from '@agilepoint/utils-common';
import { ensureDateTime, IDate } from '@agilepoint/utils-date-time';
import { ErrorMessage } from '@hookform/error-message';
import { Controller, useFormContext, UseControllerReturn } from 'react-hook-form';
import { IBaseFormFieldProps } from './types';
import { FieldBlock, StyledErrorMessage, FieldLabel, fullWidth } from './common';
import { hasError } from './utils';
import { defaultProps } from './default-props';
import styled from 'styled-components';

export interface IDatePickerFormFieldProps extends IBaseFormFieldProps, Omit<IDatePickerProps, 'name' | 'label'> {
}

const toISOString = (date?: Date) => (date ? date.toISOString() : '');

const FullWidthDatePicker = styled(DatePicker)`
    ${fullWidth}
`;

const createOnChangeProxy = memoizeFunction((onChange: any) =>
    (selectedDates: Date[]) => onChange(selectedDates.length > 1 ? selectedDates.map(toISOString) : toISOString(selectedDates[0]))
);

const getDateTime = (date?: IDate) => (date && ensureDateTime(date));

export function DatePickerField(props: IDatePickerFormFieldProps) {
    const { className, name, defaultValue, validationRules, label, maxDate, minDate, orientation, gap, title, ...restProps } = props;
    const { control, formState: { errors } } = useFormContext();
    const error = hasError(name, errors);

    const renderDatePicker = React.useCallback(({ field: { onChange, onBlur, value } }: UseControllerReturn) => (
        <FullWidthDatePicker {...restProps as any}
            onChange={createOnChangeProxy(onChange)} maxDate={getDateTime(maxDate)} minDate={getDateTime(minDate)}
            error={error} title={title} value={getDateTime(value)} onBlur={onBlur} />
    ), [restProps, maxDate, minDate, error, title]);

    return (
        <FieldBlock gap={gap} orientation={orientation} className={className}>
            {label && label ? <FieldLabel required={validationRules?.required === true} title={title}>{label}</FieldLabel> : <></>}
            <Controller control={control} defaultValue={getDateTime(defaultValue)} name={name}
                rules={validationRules} render={renderDatePicker} />
            <ErrorMessage errors={errors} name={name} as={StyledErrorMessage} />
        </FieldBlock>
    );
}

DatePickerField.defaultProps = defaultProps;
